import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface EngineResult {
  engineId: string;
  engineName: string;
  status: 'loading' | 'success' | 'error';
  data?: {
    waMessageKey: string | null;
    debugInfo?: {
      totalMessagesFetched: number;
      top5Messages: Array<{ id: string; body: string; caption: string }>;
    };
  };
  error?: string;
  errorType?: string;
  errorCode?: string;
  errorMessage?: string;
  errorData?: any;
  errorStack?: string;
  errorSource?: string;
}

interface UnloggedMessageKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  results: EngineResult[];
  isRunning: boolean;
}

export function UnloggedMessageKeyDialog({ 
  open, 
  onOpenChange, 
  results, 
  isRunning 
}: UnloggedMessageKeyDialogProps) {
  const hasResults = results.length > 0;
  const successCount = results.filter(r => r.status === 'success' && r.data?.waMessageKey).length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const loadingCount = results.filter(r => r.status === 'loading').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Unlogged Message Key Investigation</DialogTitle>
          <DialogDescription>
            Results from checking all available engines for the message key
          </DialogDescription>
        </DialogHeader>

        {isRunning && (
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Checking {loadingCount} engine{loadingCount !== 1 ? 's' : ''}...</span>
          </div>
        )}

        {!isRunning && hasResults && (
          <div className="flex items-center gap-4 mb-4">
            <Badge variant={successCount > 0 ? "default" : "secondary"} className="bg-green-600">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Found: {successCount}
            </Badge>
            {errorCount > 0 && (
              <Badge variant="destructive">
                <XCircle className="h-3 w-3 mr-1" />
                Errors: {errorCount}
              </Badge>
            )}
          </div>
        )}

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4">
            {results.length === 0 && !isRunning && (
              <div className="text-center py-8 text-slate-500">
                No engines available to check
              </div>
            )}

            {results.map((result, index) => (
              <div key={result.engineId || index} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm">{result.engineName || result.engineId}</h4>
                    {result.status === 'loading' && (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    )}
                    {result.status === 'success' && (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    )}
                    {result.status === 'error' && (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                  {result.status === 'success' && result.data?.waMessageKey && (
                    <Badge variant="default" className="bg-green-600">
                      Key Found
                    </Badge>
                  )}
                  {result.status === 'success' && !result.data?.waMessageKey && (
                    <Badge variant="secondary">
                      Not Found
                    </Badge>
                  )}
                </div>

                {result.status === 'loading' && (
                  <div className="text-sm text-slate-500">Checking store...</div>
                )}

                {result.status === 'error' && (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium">{result.error || result.errorMessage || 'Unknown error'}</div>
                        {result.errorType && (
                          <div className="text-xs text-red-500 dark:text-red-300 mt-1">
                            Type: {result.errorType}
                          </div>
                        )}
                        {result.errorCode && (
                          <div className="text-xs text-red-500 dark:text-red-300">
                            Code: {result.errorCode}
                          </div>
                        )}
                        {result.errorSource && (
                          <div className="text-xs text-red-500 dark:text-red-300">
                            Source: {result.errorSource}
                          </div>
                        )}
                        {result.errorStack && (
                          <details className="mt-2">
                            <summary className="text-xs text-red-500 dark:text-red-300 cursor-pointer hover:underline">
                              View Stack Trace
                            </summary>
                            <pre className="mt-2 text-xs bg-red-50 dark:bg-red-900/20 p-2 rounded overflow-x-auto max-h-40 overflow-y-auto">
                              {result.errorStack}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {result.status === 'success' && result.data && (
                  <div className="space-y-3">
                    {result.data.waMessageKey ? (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                        <div className="text-sm font-medium text-green-900 dark:text-green-200 mb-1">
                          Message Key Found
                        </div>
                        <code className="text-xs bg-white dark:bg-slate-800 px-2 py-1 rounded font-mono text-green-800 dark:text-green-300 break-all">
                          {result.data.waMessageKey}
                        </code>
                      </div>
                    ) : (
                      <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800">
                        <div className="text-sm font-medium text-yellow-900 dark:text-yellow-200">
                          Message Key Not Found
                        </div>
                      </div>
                    )}

                    {result.data.debugInfo && (
                      <>
                        <Separator />
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-slate-700 dark:text-slate-300">
                            Debug Information
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-slate-500 dark:text-slate-400">Total Messages:</span>
                              <span className="ml-2 font-medium">{result.data.debugInfo.totalMessagesFetched}</span>
                            </div>
                          </div>

                          {result.data.debugInfo.top5Messages.length > 0 && (
                            <div className="mt-3">
                              <div className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Top 5 Messages Checked:
                              </div>
                              <div className="space-y-2">
                                {result.data.debugInfo.top5Messages.map((msg, idx) => (
                                  <div key={idx} className="p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs">
                                    <div className="font-mono text-slate-600 dark:text-slate-400 mb-1">
                                      {msg.id}
                                    </div>
                                    {msg.body && (
                                      <div className="text-slate-700 dark:text-slate-300 mb-1 max-w-md">
                                        <span className="font-semibold">Body:</span>{" "}
                                        <span className="truncate inline-block align-bottom max-w-full">{msg.body}</span>
                                      </div>
                                    )}
                                    {msg.caption && (
                                      <div className="text-slate-700 dark:text-slate-300 max-w-md">
                                        <span className="font-semibold">Caption:</span>{" "}
                                        <span className="truncate inline-block align-bottom max-w-full">{msg.caption}</span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

